import { Injectable } from '@angular/core';
import { equalTo, orderByChild } from '@angular/fire/database';
import { Supplement, SupplementAttachment } from '../models';
import { LocalAttachment } from './camera.service';
import { DatabaseService } from './database.service';
import { HelperService } from './helper.service';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root'
})
export class SupplementService {

  constructor(
    private dbService: DatabaseService,
    private helperService: HelperService,
    private storageService: StorageService,
  ) { }
   
    getSupplementAsPromise(supplementId): Promise<Supplement> {
      return this.dbService.getSnapshot(`supplements/${supplementId}`);
    }  

    createSupplement(supplement: Partial<Supplement>) {    
      return this.dbService.pushByRef('supplements', supplement);  
    }

    updateSupplement(supplementId: string, data: Partial<Supplement>) {
      return this.dbService.updateRef(`supplements/${supplementId}`, data);
    }

    deleteSupplement(supplementId: string) {
      return this.dbService.removeRef(`supplements/${supplementId}`);
    }

    public async getOrganizationSupplements(organization, arr: boolean = false) {
      const snap = await this.dbService.getSnapshot(`supplements`, orderByChild("organization"), equalTo(organization));
      return arr && snap? this.helperService.transformFirebaseObjectToArray(snap) : snap;
    }

    public async getOrganizationSupplementsAsPromise(organization) {
      const snap = await this.dbService.getSnapshot(`supplements`, orderByChild("organization"), equalTo(organization));
      return snap?  this.helperService.transformFirebaseObjectToArray(snap) : [];
    }

    async addSupplementAttachment(
      organizationId: string,
      supplementId: string,
      attachment: LocalAttachment,
    ): Promise<string> {
      const storagePath = `organizations/${organizationId}/supplements/${supplementId}/${attachment.name}`;
      let attachmentRef: string;

      if (attachment.file) {
        attachmentRef = await this.storageService.fbPutFile(storagePath, attachment.file);
      }

      if (attachment.nativePath) {
        attachmentRef = await this.storageService.fpPutNativeFile(storagePath, attachment.nativePath);
      }

      if (!attachmentRef) {
        throw new Error('Failed to upload attachment');
      }

      const resp = await this.dbService.pushByRef(`supplements/${supplementId}/attachments`, {
        creationDate: new Date().toISOString(),
        url: attachmentRef,
        fileName: attachment.name,
        type: attachment.type,
      });
      return resp.key;
    }

    async processSupplementAttachments(
      supplementId: string,
      organizationId: string,
      attachments: LocalAttachment[] = [],
    ): Promise<void> {
      await Promise.all(
        attachments.map((a) => this.addSupplementAttachment(organizationId, supplementId, a)),
      );
    }

    /** Upload attachment for a supplement row stored only on the protocol (not a library supplement doc). */
    async uploadProtocolEmbeddedSupplementAttachment(
      organizationId: string,
      attachment: LocalAttachment,
    ): Promise<SupplementAttachment> {
      const folder = `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
      const storagePath = `organizations/${organizationId}/protocol-supplement-attachments/${folder}/${attachment.name}`;
      let attachmentRef: string;

      if (attachment.file) {
        attachmentRef = await this.storageService.fbPutFile(storagePath, attachment.file);
      }

      if (attachment.nativePath) {
        attachmentRef = await this.storageService.fpPutNativeFile(storagePath, attachment.nativePath);
      }

      if (!attachmentRef) {
        throw new Error('Failed to upload attachment');
      }

      return {
        creationDate: new Date().toISOString(),
        url: attachmentRef,
        fileName: attachment.name,
        type: attachment.type,
      };
    }
}
